import { DatePipe } from '@angular/common';
import { inject, Inject, Injectable, OnDestroy, Renderer2, RendererFactory2, DOCUMENT } from '@angular/core';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { PrerenderService } from '@df/browser/storage/prerender/prerender.service';
import { Listing, LISTING_UPDATE_EVENT } from '@df/catalog-ui/listing/listing';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { Category } from '@df/catalog/category/category';
import { ICrumb, Model } from '@df/catalog/model';
import { IProductDataV2, IProductDataV3, Product } from '@df/catalog/product/product';
import { IRating, Review } from '@df/catalog/review/review';
import { ReviewResource } from '@df/catalog/review/review.resource';
import { IDay, IStoreDataV3, Store } from '@df/catalog/store/store';
import { App } from '@df/core/app';
import { CacheService } from '@df/core/cache/cache';
import { NCore } from '@df/core/df-core.interfaces';
import { Meta } from '@df/core/meta/meta';
import { StructuralData } from '@df/core/meta/structural-data/structural-data.namespace';
import { Acl } from '@df/core/service/acl';
import { Client } from '@df/core/service/client';
import { Url } from '@df/core/service/url';
import { TimeoutService } from '@df/ng/timeout.service';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import compact from 'lodash-es/compact';
import each from 'lodash-es/each';
import find from 'lodash-es/find';
import groupBy from 'lodash-es/groupBy';
import isArray from 'lodash-es/isArray';
import sortBy from 'lodash-es/sortBy';
import without from 'lodash-es/without';
import { Subscription } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class StructuralDataService implements OnDestroy {
    /**
     * Array with elements that are already placed as scripts
     *
     * interface placedElement {
     *
     * @type {placedElement[]}
     */
    protected _placedElements: StructuralData.IPlacedElement[] = [];
    /**
     * Array with data elements that should exist as ld+json data scripts
     *
     * @type {IStructuralData[]}
     */
    protected _currentElements: StructuralData.IData[] = [];

    /**
     * Currently processed model id
     */
    protected lastMode: Model | undefined;

    /**
     * Custom schema
     */
    protected currentCustomSchema?: StructuralData.IData<StructuralData.Type>[];

    /**
     * Listing schema
     */
    protected listingElement?: StructuralData.IData<StructuralData.Type>;

    protected updateScripts = this.timeoutService.debounce(() => this._updateScripts(), 500);

    private renderer: Renderer2 = this.rendererFactory.createRenderer(null, null);

    private options = inject(NCore.OPTIONS);

    protected subscription = new Subscription();

    private release?: () => void;

    constructor(
        protected meta: Meta,
        protected client: Client,
        protected acl: Acl,
        protected app: App,
        protected url: Url,
        protected cacheService: CacheService,
        protected reviewResource: ReviewResource,
        private timeoutService: TimeoutService,
        private dfApiConfigService: DfApiConfigService,
        private rendererFactory: RendererFactory2,
        private prerenderService: PrerenderService,
        @Inject(DOCUMENT) readonly document: Document
    ) {}

    appInitialize() {
        if (!this.options.structuralData?.noListingTracking) {
            this.setListingTracking();
        }

        this.subscription.add(
            this.meta.subscribe(() => {
                this.lockSsr();
                this.currentCustomSchema = [];
                this.update();
            })
        );
    }

    private lockSsr() {
        this.unlockSsr();
        this.prerenderService.wait(
            new Promise(resolve => {
                this.release = () => {
                    resolve(true);
                };
            })
        );
    }

    private unlockSsr() {
        this.release?.();
        this.release = undefined;
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Adds sctructural data to page
     */
    add(data: StructuralData.IData<StructuralData.Type>): StructuralData.IData<StructuralData.Type> {
        this._currentElements.push(data);
        this.updateScripts();
        return data;
    }

    protected setListingTracking() {
        // listeners
        let lastCategory: Category;
        this.subscription.add(
            this.meta.subscribe(() => {
                if (!this.meta.model || this.meta.model !== lastCategory) {
                    this.listingElement = undefined;
                }
            })
        );
        this.subscription.add(
            this.app.events.on(LISTING_UPDATE_EVENT).subscribe((listing: Listing) => {
                if (listing.config.custom?.isMain && listing.config.source instanceof Category) {
                    lastCategory = listing.config.source as Category;

                    if (this.listingElement) {
                        this._currentElements = without(this._currentElements, this.listingElement);
                    }

                    this.listingElement = this.add(this.getListingData(listing as unknown as ProductListing<Product<IProductDataV2>>));
                }
            })
        );

        this.update();
    }
    /////////////////////////////////////////////////////////////////////
    ///
    /// SCRIPT TAGS MANIPULATIOn
    ///
    ////////////////////////////////////////////////////////////////////

    /**
     * Syncs ld+json scripts with _currentElements array
     */
    private _updateScripts(): void {
        const allHashes: string[] = [];
        const overrides = this.currentCustomSchema
            ? Array.isArray(this.currentCustomSchema)
                ? [...this.currentCustomSchema]
                : [this.currentCustomSchema]
            : [];

        const finalElements = this.currentCustomSchema
            ? this._currentElements.map(data => {
                  // check for override
                  const itemOverrideIndex = overrides.findIndex(i => i['@type'] === data['@type']);
                  if (itemOverrideIndex > -1) {
                      const itemOverride = overrides.splice(itemOverrideIndex, 1)[0];
                      return { ...data, ...itemOverride };
                  }
                  return data;
              })
            : this._currentElements;

        finalElements.push(...overrides);
        finalElements.forEach(data => {
            const json = JSON.stringify(data);
            if (!this._placedElements.find(i => i.json === json)) {
                this._placedElements.push({
                    json,
                    script: this._addScript(json)
                });
            }
            allHashes.push(json);
        });
        // cleanup
        this._placedElements = this._placedElements.filter(element => {
            if (!allHashes.includes(element.json)) {
                try {
                    this.renderer.removeChild(this.document.head, element.script);
                } catch (err) {}
                return false;
            }
            return true;
        });

        this.unlockSsr();
    }

    /**
     * Adds ld+json script to head with data passed in argument
     * @param {string} json Json data to pass to script
     * @return {domElement} Dom element with script
     */
    protected _addScript(json: string): HTMLScriptElement {
        const script = this.renderer.createElement('script');
        script.type = 'application/ld+json';
        script.innerHTML = json;
        this.renderer.appendChild(this.document.head, script);
        return script;
    }

    /**
     * Removes all data scripts
     */
    clear(): void {
        this._currentElements = [];
        // _.invoke(this._promises, "cancel");
        // this._promises = [];
        if (this.listingElement) {
            this._currentElements.push(this.listingElement);
        }
        this.updateScripts();
    }

    /////////////////////////////////////////////////////////////////////
    ///
    /// DATA INJECOTRS
    ///
    ////////////////////////////////////////////////////////////////////

    /**
     * Set structural data to match requested model
     */
    update(...args) {
        const model = this.meta.model;

        if (model && model.data) {
            if (!(this.lastMode && this.lastMode.type === model.type && this.lastMode.id === model.id)) {
                this.clear();
                this.addModelData(model);
                this.addBreadcrumbsData(this.meta.state!.breadcrumbs);
            }
        } else {
            this.clear();
        }
        this.lastMode = model;
    }

    protected addModelData(model: Model) {
        switch (model.type) {
            case 'product':
                if (this.options.structuralData?.useProductGroup) {
                    this.addProductGroupData(<Product<IProductDataV3>>model);
                } else {
                    this.addProductData(<Product<IProductDataV3>>model);
                }
                break;
            case 'store':
                this.addStoreData(model as Store<IStoreDataV3>);
                break;
        }
    }

    /////////////////////////////////////////////////////////////////////
    ///
    /// GENERAL
    ///
    ////////////////////////////////////////////////////////////////////

    /**
     * Adds app data script
     */
    addAppData(): void {
        const data: StructuralData.IAppData = this.getAppData();
        this.add(data);
    }

    protected getAppData(): StructuralData.IAppData {
        return {
            '@context': 'http://schema.org',
            '@type': StructuralData.Type.website,
            url: this.url.storeUrl,
            name: this.app.data.name,
            potentialAction: {
                '@type': StructuralData.Type.search,
                target: this.app.url.toUrl('/search/{search_term_string}'),
                'query-input': 'required name=search_term_string'
            }
        };
    }

    /**
     * Adds organization data
     */
    addOrganizationData() {
        const data = this.getOrganizationData();

        const websiteReviews = this.app.data.reviews;
        if (websiteReviews && websiteReviews.rating_summary && websiteReviews.reviews_count) {
            this.injectAggregateRating(data, {
                count: websiteReviews.reviews_count,
                rating: websiteReviews.rating_summary
            });
        }

        this.injectReviews(
            data,
            () =>
                this.reviewResource.loadForMerchant({
                    limit: 1,
                    verbosity: 3
                }),
            'lastMerchantReviews'
        );

        this.add(data);
    }

    getOrganizationData(): StructuralData.IOrganizationData {
        const data: StructuralData.IOrganizationData = {
            '@context': 'http://schema.org',
            '@type': StructuralData.Type.organization,
            name: this.app.data.name || '',
            url: this.app.url.storeUrl
        };

        const logo = this.getOrganizationLogo();
        if (logo) {
            data.logo = logo;
        }

        const phone = this.app.data.phone;

        if (phone) {
            const phones: StructuralData.IPhoneNumber[] = isArray(phone) ? phone : [{ no: phone, type: 'customer service' }];
            data.contactPoint = compact(
                phones.map(i => {
                    if (i.no) {
                        return <StructuralData.IContactPoint>{
                            '@type': StructuralData.Type.contactPoint,
                            telephone: i.no ? (typeof i.no === 'string' ? i.no.replace(/\s/g, '') : i.no) : null,
                            contactType: i.type
                        };
                    }
                    return undefined;
                })
            )[0];
        }
        return data;
    }

    getOrganizationLogo(): string | undefined {
        const logo = this.app.data.logo;
        if (logo) {
            return this.app.url.toMediaUrl(logo);
        }
        return undefined;
    }

    /**
     * Add schema overrides
     * You can pass schema mtaching exiting items to override existing data
     */
    setCustomSchema(schema?: string | StructuralData.IData<StructuralData.Type> | StructuralData.IData<StructuralData.Type>[]) {
        if (schema) {
            if (typeof schema === 'string' && schema) {
                try {
                    schema = JSON.parse(schema);
                } catch (er) {
                    if (this.app.isPlatformBrowser) {
                        this.app.logger.error('Invalid custom schema JSON', schema);
                    }
                    schema = [];
                }
            }
            this.currentCustomSchema = Array.isArray(schema) ? schema : ([schema] as StructuralData.IData<StructuralData.Type>[]);
        } else {
            this.currentCustomSchema = [];
        }
        this.updateScripts();
    }

    /////////////////////////////////////////////////////////////////////
    ///
    /// BREADCRUMBS
    ///
    ////////////////////////////////////////////////////////////////////

    /**
     * Adds breadcrumbs data
     * @param {label : string, url : string} breadcrumbs
     */

    addBreadcrumbsData(breadcrumbs?: ICrumb[]): void {
        if (breadcrumbs) {
            const items = this.getBreadcrumbsData(breadcrumbs);

            if (items && items.length) {
                const data: StructuralData.IBreadcrumbsListData = {
                    '@context': 'http://schema.org',
                    '@type': StructuralData.Type.breadcrumbList,
                    itemListElement: items
                };
                this.add(data);
            }
        }
    }

    getBreadcrumbsData(breadcrumbs: ICrumb[]): StructuralData.IBreadcrumbsItemData[] {
        return breadcrumbs.map((crumb: ICrumb, index: number) => {
            return {
                '@type': StructuralData.Type.breadcrumbItem,
                position: index + 1,
                item: {
                    '@id': this.app.url.toUrl(crumb.url),
                    name: crumb.label
                }
            };
        });
    }

    /////////////////////////////////////////////////////////////////////
    ///
    /// PRODUCT
    ///
    ////////////////////////////////////////////////////////////////////

    /**
     * Adds product data
     */
    addProductData(product: Product<IProductDataV3>) {
        const productData = product.data;

        if (productData) {
            const data = this.getProductData(product);

            this.injectProductReviews(product, data);

            // mind that data will be changed async in injectRevies, we will update it later
            this.add(data);
        }
    }

    getProductData(product: Product<IProductDataV3 | IProductDataV2>): StructuralData.IProductData {
        const productData = product.data as IProductDataV3;
        const meta = productData.meta;
        const data: StructuralData.IProductData = {
            '@context': 'http://schema.org',
            '@type': StructuralData.Type.product,
            '@id': productData.sku,
            name: productData.name,
            sku: productData.sku,
            url: this.url.toUrl(productData.url || '')
        };

        const image = meta ? meta.ogimage || productData.image : productData.image;
        if (image) {
            data.image = [this.app.url.toMediaUrl(image)];
        }

        if (meta && meta.description) {
            data.description = meta.description;
        }

        const brand = product.brand;
        if (brand) {
            data.brand = {
                '@type': StructuralData.Type.brand,
                name: brand
            };
        }

        if (productData.price) {
            data.offers = {
                '@type': StructuralData.Type.offer,
                priceCurrency: this.app.data.currency.base,
                price: productData.price ? productData.price.toString() : '',
                itemCondition: StructuralData.ItemCondition.new,
                availability: this.app.meta.getProductAvailability(product.id) as StructuralData.ItemAvailability,
                seller: productData.price ? this.getOrganizationData() : undefined
            };
        }
        return data;
    }

    /**
     * Adds product group data
     */
    addProductGroupData(product: Product<IProductDataV3>) {
        const configurableProduct = product?.isConfigurable ? product : (product.parent as Product<IProductDataV3>);
        if (!configurableProduct?.isConfigurable) {
            // if not configurable, just add product data
            this.addProductData(product);
        } else {
            const productData = product.data;

            if (productData) {
                const data = this.getProductGroupData(configurableProduct);

                this.injectProductReviews(configurableProduct, data!);

                // mind that data will be changed async in injectRevies, we will update it later
                this.add(data!);
            }
        }
    }

    /**
     * Get product group data
     */
    getProductGroupData(product: Product<IProductDataV3 | IProductDataV2>): StructuralData.IProductGroupData | undefined {
        if (!product?.isConfigurable) {
            return undefined;
        }
        const parentProductData: Object = this.getProductData(product);
        parentProductData['@type'] = StructuralData.Type.productGroup;
        parentProductData['productGroupID'] = product.data?.sku?.replace(' ', '');
        parentProductData['variesBy'] = product.configurableAttributeCodes?.map(attr => 'https://schema.org/' + attr);
        parentProductData['hasVariant'] = product.children.map(variant => {
            const variantData: StructuralData.IProductDetailsData = this.getProductData(variant as Product<IProductDataV3>);
            delete variantData['@context'];
            product.configurableAttributeCodes?.forEach(attr => {
                variantData[attr] = optionLabelPipe(variant.data?.[attr], attr);
            });
            return variantData;
        });
        return parentProductData as StructuralData.IProductGroupData;
    }

    // Inject product reviews
    injectProductReviews(product: Product<IProductDataV3>, data: StructuralData.IProductData | StructuralData.IProductGroupData) {
        const productData = product.data;

        if (productData) {
            this.injectAggregateRating(data, productData.reviews);
            this.injectReviews(
                data,
                () =>
                    this.reviewResource.loadByProduct(
                        product.family.map(i => i.id),
                        {
                            verbosity: 3,
                            limit: 1
                        }
                    ),
                'lastProductReviews' + productData.id
            );
        }
    }

    /////////////////////////////////////////////////////////////////////
    ///
    /// LISTING
    ///
    /////////////////////////////////////////////////////////////////////

    getListingData(listing: ProductListing<Product<IProductDataV2>>): StructuralData.IListingData {
        const products = listing.getSourceModels();
        const data: StructuralData.IListingData = {
            '@context': 'http://schema.org',
            '@type': StructuralData.Type.listing,
            url: this.url.toUrl(this.url.getPath()),
            numberOfItems: products.length + '',
            itemListElement: products
                .filter(p => !p.data?.['isBanner']) // skip banners
                .map(
                    (product, index): StructuralData.IItemListData => ({
                        '@type': StructuralData.Type.listItem,
                        position: index + 1,
                        item: this.getProductData(product)
                    })
                )
        };

        return data;
    }

    /////////////////////////////////////////////////////////////////////
    ///
    /// STORE
    ///
    ////////////////////////////////////////////////////////////////////

    addStoreData(store: Store<IStoreDataV3>): void {
        const storeData = store.data;
        if (storeData) {
            const region = store.data ? store.data.state : undefined;
            const promise = region ? this.dfApiConfigService.loadCountries() : undefined;
            const addData = () => {
                const localBusinessData = this.getStoreData(store);
                if (localBusinessData) {
                    this.add(localBusinessData);
                }
            };
            if (promise) {
                promise.finally(addData);
            } else {
                addData();
            }
        }
    }

    getStoreData(store: Store): StructuralData.ILocalBusinessData | undefined {
        const storeData = store.data;
        if (storeData) {
            const data: StructuralData.ILocalBusinessData = {
                '@context': 'http://schema.org',
                '@type': StructuralData.Type.store,
                name: storeData.store_name
            };

            const { country_id, state, latitude, longitude, city, address, postcode, url, phone } = storeData;
            const addressRegion = state && country_id ? this.getRegionCode(state, country_id) : undefined;
            const addressData: StructuralData.IAddressData = {
                '@type': StructuralData.Type.address,
                streetAddress: address,
                addressLocality: city,
                addressCountry: country_id,
                addressRegion,
                postalCode: postcode
            };
            each(addressData as { [key: string]: any }, (value, key) => {
                if (!value) {
                    delete addressData[key];
                }
            });
            if (Object.keys(addressData).length > 1) {
                data.address = addressData;
            }
            if (latitude && longitude) {
                data.geo = {
                    '@type': StructuralData.Type.geo,
                    latitude,
                    longitude
                };
            }

            if (url) {
                const path = this.app.url.toUrl(url);
                data.url = path;
                data['@id'] = path;
            }
            if (phone) {
                data.telephone = phone;
            }
            const openingHoursSpecificationData = this.getOpeningHoursSpecificationData(store);
            if (openingHoursSpecificationData) {
                data.openingHoursSpecification = openingHoursSpecificationData;
            }
            return data;
        }
        return undefined;
    }

    getRegionCode(regionName: string, countryId: string): string | undefined {
        const foundCountry = find(
            this.dfApiConfigService.countries || [],
            country => country.code.toLowerCase() === countryId.toLowerCase()
        );
        if (foundCountry && foundCountry.regions && foundCountry.regions.length) {
            const foundRegion = find(foundCountry.regions, region => region.name.toLowerCase() === regionName.toLowerCase());
            if (foundRegion) {
                return foundRegion.code || foundRegion.name;
            }
        }
        return undefined;
    }

    getOpeningHoursSpecificationData(store: Store): StructuralData.IOpeningHoursSpecificationData[] | undefined {
        const days = store.openingHours && sortBy(store.openingHours.slice(0, 7), day => day.date && day.date.getDay());
        if (!days || !days.length) {
            return undefined;
        }
        days.push(<IDay>days.shift());

        const groupedByOpeningHours = groupBy(
            days.filter(i => i.status === 2 || (i.close && i.open)),
            day => (day.status === 1 ? (day.open as string) + (day.close as string) : 'closed')
        );
        return compact(
            Object.values(groupedByOpeningHours).map(groupedDays => {
                const first = groupedDays[0];
                if (first) {
                    const datePipe = new DatePipe(this.app.data.locale || 'en-GB');
                    const daysOfWeek = compact(groupedDays.map(day => day.date && datePipe.transform(day.date, 'EEEE')));
                    if (daysOfWeek.length) {
                        const openingHoursData: StructuralData.IOpeningHoursSpecificationData = {
                            '@type': StructuralData.Type.openingHours,
                            dayOfWeek: daysOfWeek.length > 1 ? daysOfWeek : daysOfWeek[0],
                            opens: first.status === 1 ? (first.open as string) : '00:00',
                            closes: first.status === 1 ? (first.close as string) : '00:00'
                        };
                        return openingHoursData;
                    }
                }
                return undefined;
            })
        );
    }

    /////////////////////////////////////////////////////////////////////
    ///
    /// DATA PROCESSING HELPERS
    ///
    ////////////////////////////////////////////////////////////////////

    /**
     * Loads reviews and injects them into structural data
     * @param  {object} data     Partial data to inject reviews into
     * @param  {function} provider Promise returning load function
     * @param  {string} cName    Cachec name for that reviews provider
     */
    protected injectReviews(
        data: StructuralData.IProductData | StructuralData.IOrganizationData | StructuralData.IProductGroupData,
        provider: () => Promise<Review[]>,
        cName: string
    ) {
        const cachedReviews = this.cacheService.get(cName);

        if (cachedReviews) {
            data.review = cachedReviews[0];
        } else {
            const promise = provider();
            if (promise) {
                promise.then(reviewsModels => {
                    const reviews = this._normalizeReviews(reviewsModels);
                    this.cacheService.set(cName, reviews);
                    data.review = reviews[0];
                });
            }
        }
    }

    protected _normalizeReviews(reviews: Review[]): StructuralData.IReviewData[] {
        return compact<StructuralData.IReviewData>(
            reviews.map((review: Review): StructuralData.IReviewData | undefined => {
                if (review && review.data) {
                    const data: StructuralData.IReviewData = {
                        '@type': StructuralData.Type.review,
                        author: {
                            '@type': StructuralData.Type.person,
                            name: review.data.author || ''
                        },
                        datePublished: review.data.date,
                        reviewBody: review.data.content,
                        reviewRating: {
                            '@type': StructuralData.Type.rating,
                            bestRating: 100 / 20,
                            ratingValue: review.data.rating / 20,
                            worstRating: 20 / 20
                        }
                    };
                    return data;
                }

                return undefined;
            })
        );
    }

    /**
     * Injects aggregate rating into data model
     * @param  {object} data
     * @param  {object} reviewsSummary
     */
    protected injectAggregateRating(
        data: StructuralData.IOrganizationData | StructuralData.IProductData | StructuralData.IProductGroupData,
        reviewsSummary: IRating | undefined
    ): void {
        if (reviewsSummary && reviewsSummary.count && reviewsSummary.rating) {
            data.aggregateRating = {
                '@type': StructuralData.Type.aggregateRating,
                ratingValue: Math.round(reviewsSummary.rating) / 20,
                bestRating: 5,
                worstRating: 1,
                reviewCount: reviewsSummary.count
            };
        }
    }
}
